import { ExternalLink, MapPin, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { agendaKey, agendaStyle } from "@/lib/agendaColors";
import { StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const EventRow = ({ e, onEdit, onDeleted }) => {
  const { can, me } = useAuth();
  const remove = async () => {
    try { await (e.kind === "activity" ? api.post(`/activities/${e.id}/cancel`, {}) : api.delete(`/calendar/events/${e.id}`)); toast.success("Appuntamento eliminato"); onDeleted(); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4" data-testid={`calendar-event-${e.id}`}>
      <span className={cn("hidden h-8 w-1 shrink-0 rounded-full sm:block", agendaStyle(e).dot)} data-testid={`calendar-event-color-${e.id}`} data-kind={agendaKey(e)} />
      <span className="w-28 shrink-0 font-mono text-xs font-semibold text-ink">{e.is_all_day ? "Tutto il giorno" : `${fmtTime(e.start_at)} – ${fmtTime(e.end_at)}`}</span>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate text-sm font-semibold text-ink"><span className={cn("h-2 w-2 shrink-0 rounded-full sm:hidden", agendaStyle(e).dot)} />
          {e.kind === "activity" ? <button type="button" onClick={() => onEdit(e)} className="truncate text-left hover:text-brand-700 hover:underline" data-testid={`calendar-open-${e.id}`}>{e.subject}</button> : e.subject}</p>
        <p className="flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
          {e.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</span>}
          {e.company && <span>{e.company}</span>}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {e.kind !== "activity" && e.origin === "onsuite" && <StatusBadge tone="brand" dot={false}>Creato da ON! Suite</StatusBadge>}
        {e.web_link && <Button asChild size="icon" variant="ghost" aria-label="Apri in Outlook"><a href={e.web_link} target="_blank" rel="noopener noreferrer" data-testid={`calendar-open-outlook-${e.id}`}><ExternalLink /></a></Button>}
        {can("calendar:update") && !e.intervention_id && <Button size="icon" variant="ghost" aria-label="Modifica" onClick={() => onEdit(e)} data-testid={`calendar-edit-${e.id}`}><Pencil /></Button>}
        {can("calendar:delete") && !e.intervention_id && (
          <AlertDialog>
            <AlertDialogTrigger asChild><Button size="icon" variant="ghost" aria-label="Elimina" data-testid={`calendar-delete-${e.id}`}><Trash2 /></Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare "{e.subject}"?</AlertDialogTitle>
                <AlertDialogDescription>L'appuntamento verrà eliminato anche dal tuo {me?.tenant?.slug ? "calendario" : "calendario Outlook"}.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="calendar-delete-confirm">Elimina</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </li>
  );
};
